import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getBinderByShareToken } from "@/lib/binder.functions";
import { BinderGrid, type CardRow } from "@/routes/binder";
import { BINDER_CAPACITY, BINDER_PAGES } from "@/lib/binder-shared";

export const Route = createFileRoute("/binder-share/$token")({
  head: ({ params }) => ({
    meta: [
      { title: `Shared Palworld Card Binder (link ${params.token.slice(0, 8)}) — Pal Stars` },
      { name: "description", content: `A Pal Stars collector shared their Palworld card binder via private link ${params.token.slice(0, 8)} — flip through all 108 pockets, no account needed.` },
      { property: "og:title", content: `Shared Palworld card binder (link ${params.token.slice(0, 8)}) — Pal Stars` },
      { property: "og:description", content: `108 Palworld cards from private binder link ${params.token.slice(0, 8)}, filed pocket by pocket on Pal Stars.` },
      { property: "og:url", content: `https://palstars.com/binder-share/${params.token}` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/binder-share" }],
  }),
  component: BinderShareTokenPage,
});

function BinderShareTokenPage() {
  const { token } = Route.useParams();
  const fetchBinder = useServerFn(getBinderByShareToken);
  const [state, setState] = useState<{ owner: string; cards: CardRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    fetchBinder({ data: { token } })
      .then((r) => setState(r as unknown as { owner: string; cards: CardRow[] }))
      .catch(() => setError("This share link has expired, been revoked, or never existed."));
  }, [token, fetchBinder]);

  const map = useMemo(() => {
    const m = new Map<number, CardRow>();
    (state?.cards ?? []).forEach((c) => { if (c.binder_slot !== null) m.set(c.binder_slot, c); });
    return m;
  }, [state]);

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="text-3xl font-bold">This binder link is no longer active</h1>
        <p className="aeo-answer mt-3 text-sm text-muted-foreground">
          Pal Stars binder share links expire after 30 days and can be revoked by their owner at any time. Ask the
          collector for a fresh link, or open your own free {BINDER_CAPACITY}-pocket binder.
        </p>
        <Link to="/pack-opening" className="mt-5 inline-block rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:brightness-110">
          Open a free booster pack
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8 space-y-6">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Shared collector binder</p>
        <h1 className="mt-2 text-3xl font-bold">{state?.owner ?? "Trainer"}'s Palworld card binder</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {map.size} of {BINDER_CAPACITY} pockets filled · page {page + 1} of {BINDER_PAGES} · private share link
        </p>
      </header>

      <div className="glass rounded-2xl p-4 sm:p-6">
        <BinderGrid page={page} cards={map} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-40">
          ← Previous page
        </button>
        <button onClick={() => setPage((p) => Math.min(BINDER_PAGES - 1, p + 1))} disabled={page >= BINDER_PAGES - 1} className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-40">
          Next page →
        </button>
        <Link to="/pack-opening" className="ml-auto rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:brightness-110">
          Open your own free packs
        </Link>
      </div>
    </div>
  );
}
